import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  ArrowLeft, Send, Sparkles, Plus, Loader2, Trash2, Play,
  AlertTriangle, CheckCircle2, Building2, ChevronRight, PanelRightClose,
  PanelRightOpen, PanelLeftClose, PanelLeftOpen, ExternalLink, Copy, Check,
  Bookmark, BookmarkCheck, StickyNote
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import toast from 'react-hot-toast';
import SavedPromptsModal from '../../common/SavedPromptsModal';
import SavePromptDialog from '../../common/SavePromptDialog';
import AddToNotesModal from '../../notes/AddToNotesModal';
import RotatingStatusIndicator from '../../common/RotatingStatusIndicator';
import { AIT_STATEMENTS } from '../../common/statusStatements';

// Configure marked renderer for links opening in new tabs
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = function (href, title, text) {
  const html = originalLinkRenderer(href, title, text);
  return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

function wrapTablesForScroll(html) {
  if (typeof document === 'undefined' || !html.includes('<table')) return html;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('table').forEach((t) => {
    if (t.parentElement?.dataset?.tableScroll === 'true') return;
    const wrap = document.createElement('div');
    wrap.dataset.tableScroll = 'true';
    wrap.style.cssText = 'overflow-x:auto;max-width:100%;margin:0.5rem 0;-webkit-overflow-scrolling:touch';
    t.parentNode.insertBefore(wrap, t);
    wrap.appendChild(t);
  });
  return tmp.innerHTML;
}

function formatMarkdown(text) {
  if (typeof text !== 'string' || !text) return '';
  try {
    const rawHTML = marked(text, { gfm: true, breaks: true, renderer });
    const clean = DOMPurify.sanitize(rawHTML, {
      ADD_ATTR: ['target', 'rel'],
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'hr', 'del',
        'table', 'thead', 'tbody', 'tr', 'td', 'th', 'span', 'div', 'sup', 'sub'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
    });
    return wrapTablesForScroll(clean);
  } catch {
    return text.replace(/\n/g, '<br>');
  }
}

function AitChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const {
    chats, currentChatId, chatMessages, chatLoading,
    fetchChats, createChat, fetchChatMessages, sendChatMessage, deleteChat,
    modelScenario, fetchProperty
  } = useAit();

  const [input, setInput] = useState('');
  const [property, setProperty] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRightSidebar, setShowRightSidebar] = useState(true);
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const [scenarioResults, setScenarioResults] = useState({});
  const [runningScenario, setRunningScenario] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSaveToNotes, setShowSaveToNotes] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingContent, setSavingContent] = useState('');
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isDark = theme === 'dark';

  // Load property and chats
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchProperty(id);
      if (data) {
        setProperty(data.property);
        setBuildings(data.buildings || []);
      }
      await fetchChats(id);
      setLoading(false);
    };
    load();
  }, [id, fetchProperty, fetchChats]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Focus input when chat selected
  useEffect(() => {
    if (currentChatId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [currentChatId]);

  const handleNewChat = async () => {
    const result = await createChat(id);
    if (!result.success) toast.error('Failed to create chat');
  };

  const handleSelectChat = async (chatId) => {
    await fetchChatMessages(id, chatId);
  };

  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    const result = await deleteChat(id, chatId);
    if (result.success) toast.success('Chat deleted');
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || chatLoading) return;

    let chatId = currentChatId;
    if (!chatId) {
      const result = await createChat(id);
      if (!result.success) { toast.error('Failed to create chat'); return; }
      chatId = result.chat.id;
    }

    const message = input.trim();
    setInput('');
    await sendChatMessage(id, chatId, message);
  };

  const handleRunScenario = async (scenario) => {
    if (!scenario.building_id || !scenario.changes) return;
    setRunningScenario(scenario);
    const result = await modelScenario(id, scenario.building_id, scenario.changes);
    if (result) {
      setScenarioResults(prev => ({ ...prev, [JSON.stringify(scenario.changes)]: result }));
    } else {
      toast.error('Failed to run scenario');
    }
    setRunningScenario(null);
  };

  const handleCopy = async (msgId, rawText) => {
    const html = formatMarkdown(rawText);
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plain = tmp.textContent || tmp.innerText || rawText;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        })
      ]);
    } catch {
      // Fallback for browsers that don't support ClipboardItem
      await navigator.clipboard.writeText(plain);
    }
    setCopiedId(msgId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const parseContent = (content) => {
    if (!content) return { text: '', scenarios: [] };
    const scenarioPattern = /```scenario\s*\n([\s\S]*?)\n```/g;
    const scenarios = [];
    let match;
    while ((match = scenarioPattern.exec(content)) !== null) {
      try {
        const s = JSON.parse(match[1].trim());
        if (s.changes) scenarios.push(s);
      } catch (e) { /* skip */ }
    }
    const text = content.replace(/```scenario\s*\n[\s\S]*?\n```/g, '').trim();
    return { text, scenarios };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Left sidebar — chat list */}
      {showLeftSidebar && (
        <div className={`w-64 shrink-0 flex flex-col border-r ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          {/* Back + new chat */}
          <div className={`flex items-center justify-between px-3 py-3 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={() => navigate(`/dashboard/ait/${id}`)}
              className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button
              onClick={handleNewChat}
              className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="New chat"
            >
              <Plus size={14} />
            </button>
          </div>

          {/* Chat list */}
          <div className="flex-1 overflow-y-auto">
            {chats.length === 0 ? (
              <p className={`text-xs text-center py-6 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                No conversations yet
              </p>
            ) : (
              chats.map(chat => (
                <button
                  key={chat.id}
                  onClick={() => handleSelectChat(chat.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-left border-b ${
                    chat.id === currentChatId
                      ? isDark ? 'bg-blue-900/20 border-blue-800/30 text-blue-400' : 'bg-blue-50 border-blue-100 text-blue-700'
                      : isDark ? 'border-gray-800 hover:bg-gray-800 text-gray-300' : 'border-gray-100 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{chat.title}</p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {chat.message_count || 0} messages
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                    className={`p-1 rounded shrink-0 ml-1 ${isDark ? 'hover:bg-gray-700 text-gray-600' : 'hover:bg-gray-200 text-gray-400'}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLeftSidebar(!showLeftSidebar)}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title={showLeftSidebar ? 'Hide chat list' : 'Show chat list'}
            >
              {showLeftSidebar ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
            </button>
            <Sparkles size={18} className="text-blue-500" />
            <h2 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              AIT Assistant — {property?.name}
            </h2>
          </div>
          <button
            onClick={() => setShowRightSidebar(!showRightSidebar)}
            className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            title={showRightSidebar ? 'Hide property info' : 'Show property info'}
          >
            {showRightSidebar ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!currentChatId && chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles size={48} className={`mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
              <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                AIT Compliance Assistant
              </h3>
              <p className={`text-sm max-w-md mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Ask questions about your property's Average Income Test compliance, get designation strategy advice, or run what-if scenarios.
              </p>
              <div className={`text-xs space-y-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                <p>"What's my current compliance position?"</p>
                <p>"How can I add a 70% unit without breaching?"</p>
                <p>"What would happen if I redesignate Unit 301 to 50%?"</p>
              </div>
              <button
                onClick={handleNewChat}
                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Start a conversation
              </button>
            </div>
          ) : (
            <>
              {chatMessages.map((msg) => {
                const { text, scenarios } = msg.is_user
                  ? { text: msg.content, scenarios: [] }
                  : parseContent(msg.content);
                const citations = (!msg.is_user && msg.citations) ? msg.citations : [];

                return (
                  <div key={msg.id} className={`flex ${msg.is_user ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-2xl rounded-xl px-4 py-3 text-sm group relative ${
                      msg.is_user
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {/* Rendered message */}
                      {msg.is_user ? (
                        <div className="whitespace-pre-wrap break-words">{text}</div>
                      ) : (
                        <div
                          className={`prose prose-sm max-w-none break-words ${
                            isDark ? 'prose-invert' : ''
                          } prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-hr:my-3 prose-pre:my-2 prose-blockquote:my-2`}
                          dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }}
                        />
                      )}

                      {/* Save prompt button for user messages */}
                      {msg.is_user && (
                        <div className="flex justify-end mt-1 -mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => { setSavingContent(msg.content); setShowSaveDialog(true); }}
                            className="p-0.5 rounded text-blue-200 hover:text-white"
                            title="Save as prompt"
                          >
                            <Bookmark size={12} />
                          </button>
                        </div>
                      )}

                      {/* Action buttons for AI messages */}
                      {!msg.is_user && (
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => { setNoteContent(msg.content); setShowSaveToNotes(true); }}
                            className={`p-1 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                            title="Save to Notes"
                          >
                            <StickyNote size={14} />
                          </button>
                          <button
                            onClick={() => handleCopy(msg.id, msg.content)}
                            className={`p-1 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                            title="Copy message"
                          >
                            {copiedId === msg.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                        </div>
                      )}

                      {/* Citations */}
                      {citations.length > 0 && (
                        <div className={`mt-3 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Sources</p>
                          <div className="space-y-0.5">
                            {citations.map((cite, idx) => (
                              <a
                                key={idx}
                                href={cite.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-1 text-xs hover:underline ${
                                  isDark ? 'text-blue-400' : 'text-blue-600'
                                }`}
                              >
                                <ExternalLink size={10} className="shrink-0" />
                                <span className="truncate">{cite.title || cite.url}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Scenario cards */}
                      {scenarios.map((scenario, idx) => {
                        const key = JSON.stringify(scenario.changes);
                        const result = scenarioResults[key];
                        const isRunning = runningScenario && JSON.stringify(runningScenario.changes) === key;

                        return (
                          <div
                            key={idx}
                            className={`mt-3 p-3 rounded-lg border ${
                              isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'
                            }`}
                          >
                            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {scenario.description}
                            </p>

                            {result ? (
                              <div className="space-y-1.5">
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Before: </span>
                                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                      {result.before?.average_income ?? '—'}% avg
                                    </span>
                                  </div>
                                  <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>After: </span>
                                    <span className={`font-medium ${
                                      result.after?.compliant ? 'text-green-500' : 'text-red-500'
                                    }`}>
                                      {result.after?.average_income ?? '—'}% avg
                                    </span>
                                  </div>
                                </div>
                                {result.delta != null && (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    {result.after?.compliant ? (
                                      <CheckCircle2 size={14} className="text-green-500" />
                                    ) : (
                                      <AlertTriangle size={14} className="text-red-500" />
                                    )}
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                                      Delta: {result.delta > 0 ? '+' : ''}{result.delta}% | Headroom: {result.after?.headroom} | {result.after?.compliant ? 'Compliant' : 'Non-Compliant'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleRunScenario(scenario)}
                                disabled={isRunning}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isRunning ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                                Run Scenario
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {chatLoading && <RotatingStatusIndicator statements={AIT_STATEMENTS} />}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        {(currentChatId || chatMessages.length > 0) && (
          <form
            onSubmit={handleSend}
            className={`px-6 py-4 border-t shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <div className="flex items-center gap-3 max-w-3xl mx-auto">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about AIT compliance, designation strategies, what-if scenarios..."
                disabled={chatLoading}
                className={`flex-1 px-4 py-2.5 rounded-xl border text-sm ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                } disabled:opacity-50`}
              />
              <button
                type="button"
                onClick={() => setShowPromptsModal(true)}
                className={`p-2.5 rounded-xl transition-colors ${
                  isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
                title="Saved prompts"
              >
                <BookmarkCheck size={18} />
              </button>
              <button
                type="submit"
                disabled={!input.trim() || chatLoading}
                className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Right sidebar — property summary */}
      {showRightSidebar && (
        <div className={`w-72 shrink-0 flex flex-col border-l overflow-y-auto ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Property Summary
            </h3>
          </div>

          {property && (
            <div className="px-4 py-3 space-y-3">
              {/* Property info */}
              <div>
                <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {property.name}
                </h4>
                {property.address && (
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {property.address}{property.city ? `, ${property.city}` : ''} {property.state || ''}
                  </p>
                )}
              </div>

              {/* Key metrics */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Units</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {property.total_units || '—'}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Buildings</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {property.building_count || 0}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Election</p>
                  <p className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {property.msa_election === 'average_income' ? 'Avg Income' :
                     property.msa_election === '40_60' ? '40/60' :
                     property.msa_election === '20_50' ? '20/50' :
                     property.msa_election === '25_60' ? '25/60' : '—'}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Status</p>
                  <p className={`text-xs font-medium ${
                    property.compliance_status === 'compliant' ? 'text-green-500' :
                    property.compliance_status === 'breach' ? 'text-red-500' :
                    property.compliance_status === 'at_risk' ? 'text-orange-500' :
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {property.compliance_status === 'compliant' ? 'Compliant' :
                     property.compliance_status === 'breach' ? 'Breach' :
                     property.compliance_status === 'at_risk' ? 'At Risk' :
                     property.compliance_status === 'fixed_set_aside' ? 'Fixed' : 'No Data'}
                  </p>
                </div>
              </div>

              {/* Programs */}
              {property.programs?.length > 0 && (
                <div>
                  <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Programs</p>
                  <div className="flex flex-wrap gap-1">
                    {property.programs.map(p => (
                      <span key={p} className={`text-xs px-2 py-0.5 rounded-full ${
                        isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {p === 'tax_credit' ? 'Tax Credit' :
                         p === 'bonds' ? 'Bonds' :
                         p === 'home' ? 'HOME' :
                         p.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Buildings list */}
              {buildings.length > 0 && (
                <div>
                  <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Buildings</p>
                  <div className="space-y-1">
                    {buildings.map(b => (
                      <button
                        key={b.id}
                        onClick={() => navigate(`/dashboard/ait/${id}/buildings/${b.id}`)}
                        className={`w-full flex items-center justify-between p-2 rounded-lg text-left text-xs ${
                          isDark ? 'bg-gray-800 hover:bg-gray-750 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <Building2 size={12} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
                          <span>{b.name || `BIN ${b.bin_number}`}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>
                            {b.unit_count} units
                          </span>
                          <ChevronRight size={12} className={isDark ? 'text-gray-700' : 'text-gray-300'} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <SavedPromptsModal
        isOpen={showPromptsModal}
        onClose={() => setShowPromptsModal(false)}
        onUse={(content) => setInput(content)}
        currentFeature="ait"
      />
      <SavePromptDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        content={savingContent}
        featureTag="ait"
      />
      {showSaveToNotes && (
        <AddToNotesModal
          content={noteContent}
          sourceType="ait_chat"
          onClose={() => setShowSaveToNotes(false)}
          theme={isDark ? 'dark' : 'light'}
        />
      )}
    </div>
  );
}

export default AitChatPage;
